import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Award, ArrowLeft, Download, ExternalLink, BookOpen, Calendar, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyCertificates() {
  const { user, loading: authLoading, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    fetchCertificates();
  }, [user, authLoading]);

  const fetchCertificates = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/certificates`, { headers: getAuthHeader() });
      setCertificates(res.data);
    } catch (e) {
      toast.error('Failed to load certificates');
    } finally { setLoading(false); }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-playfair font-bold text-foreground">My Certificates</h1>
            <p className="text-muted-foreground text-sm mt-1">{certificates.length} certificate{certificates.length !== 1 ? 's' : ''} earned</p>
          </div>
        </div>

        {certificates.length === 0 ? (
          <div className="wm-card p-12 rounded-lg text-center">
            <Award className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">No certificates yet</h2>
            <p className="text-muted-foreground mb-6">Complete a course to earn your first certificate!</p>
            <Link to="/courses">
              <Button className="bg-primary text-primary-foreground">
                <BookOpen className="w-4 h-4 mr-2" /> Browse Courses
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {certificates.map(cert => (
              <div key={cert.id} className="wm-card rounded-lg overflow-hidden hover:ring-1 hover:ring-primary/30 transition group" data-testid="certificate-card">
                {/* Certificate Preview */}
                <div className="h-40 bg-gradient-to-br from-amber-900/30 via-primary/10 to-emerald-900/20 relative flex items-center justify-center p-6">
                  <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.03) 10px, rgba(255,255,255,0.03) 20px)' }} />
                  <div className="text-center relative z-10">
                    <Award className="w-10 h-10 text-amber-400 mx-auto mb-2" />
                    <p className="text-xs text-amber-400/80 uppercase tracking-[0.2em] font-semibold">Certificate of Completion</p>
                    <p className="text-lg font-playfair font-bold text-foreground mt-1 line-clamp-1">{cert.course_title}</p>
                  </div>
                </div>
                
                <div className="p-5">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                    <Calendar size={12} />
                    <span>Issued {new Date(cert.issued_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </div>
                  <p className="text-xs text-muted-foreground font-mono mb-4">ID: {cert.certificate_number}</p>
                  <div className="flex gap-2">
                    <Link to={`/certificate/${cert.id}`} className="flex-1">
                      <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90" size="sm" data-testid="view-cert-btn">
                        <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> View
                      </Button>
                    </Link>
                    <Link to={`/certificate/${cert.id}?print=true`}>
                      <Button variant="outline" size="sm" className="border-border text-muted-foreground" data-testid="download-cert-btn">
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
